

import { useState, useEffect } from 'react';
import AddDeviceModal from './AddDeviceModal';
import { getDeviceByUser } from '../components/DbServices';
import axios from 'axios';
import { whatsappapi } from '../api/api';
import SendMessageModal from './SendMessageModal';
import MessageLogModal from './MessageLogModal';
import ChatbotModal from './ChatbotModal';
import Layout from '../layout/Layout';
import ScheduleModal from './ScheduleModal';
import PageTransition from '../components/PageTransition';
import { getStoredUser } from '../utilities/encryption';
import Swal from 'sweetalert2';

export default function DeviceList() {
  const user = getStoredUser();
  const userId = user?.data?.id;
  
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [viewingLogs, setViewingLogs] = useState(null);
  const [activeChatbot, setActiveChatbot] = useState(null);
  const [schedule, setschedule] = useState(null);
  const [reconnectDevice, setReconnectDevice] = useState(null);
  // Helper function for Clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // Optional: You could trigger a small toast notification here
      Swal.fire("Session ID copied to clipboard!");
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  const fetchDevices = async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const res = await getDeviceByUser(userId);
      setDevices(res.data || []);
    } catch (err) {
      console.error("Failed to fetch devices", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, [userId]);

  const handleDisconnect = async (sessionId, deviceId) => {
    if (!window.confirm("Confirm logout? This will invalidate the current WhatsApp session.")) return;
    try {
      const response = await axios.post(`${whatsappapi}/device/logout`, {
        session_id: sessionId,
        device_id: deviceId
      });
      if (response.data.success) fetchDevices();
    } catch (err) {
      Swal.fire("Failed to disconnect device.");
    }
  };

  const filteredDevices = devices.filter(d => 
    d.device_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.phone_number?.includes(searchTerm)
  );

  return (
    <Layout>
      <PageTransition>
        <div className="container-fluid py-4">
          
          {/* HEADER SECTION - More Colorful */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3 className="fw-bold mb-1"><span className="color-theme">WhatsApp Controller</span> ⚡</h3>
              <p className="text-muted small fw-medium">Monitor and manage your active instance nodes.</p>
            </div>
            <button
              className="btn btn-primary rounded-pill px-4 fw-bold shadow border-0"
              style={{ background: 'linear-gradient(45deg, #4f46e5, #9333ea)' }}
              onClick={() => setShowModal(true)}
            >
              <i className="bi bi-plus-circle-fill me-2"></i> New Instance
            </button>
          </div>

          {/* MAIN TABLE DATA */}
          <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0 custom-admin-table">
                <thead style={{ backgroundColor: '#f8fafc' }}>
                  <tr className="text-muted small text-uppercase fw-bold">
                    <th className="ps-4 py-4">Identity</th>
                    <th className="py-4">Session Token</th>
                    <th className="py-4">Status</th>
                    <th className="py-4 text-center">Tools</th>
                    <th className="pe-4 py-4 text-end">Operations</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="5" className="text-center py-5 text-muted small">Loading Secure Instances...</td></tr>
                  ) : filteredDevices.length === 0 ? (
                    <tr><td colSpan="5" className="text-center py-5 text-muted small">No active instances found.</td></tr>
                  ) : (
                    filteredDevices.map(d => {
                      const isOnline = d.status?.toLowerCase().trim() === 'connected' || d.status?.toLowerCase().trim() === 'device connected';
                      return (
                        <tr key={d.id} className="border-bottom border-light">
                          <td className="ps-4 py-3">
                            <div className="d-flex align-items-center gap-3">
                              <div className={`rounded-4 d-flex align-items-center justify-content-center shadow-sm ${isOnline ? 'bg-success text-white' : 'bg-secondary text-white'}`} 
                                   style={{ width: '42px', height: '42px' }}>
                                <i className="bi bi-whatsapp fs-5"></i>
                              </div>
                              <div>
                                <div className="fw-bold text-dark small mb-0">{d.device_name}</div>
                                <div className="text-muted" style={{ fontSize: '11px' }}>+{d.phone_number} <span className="mx-1 opacity-25">|</span> Device ID: {d.id}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                                <code className="text-indigo bg-indigo-subtle px-2 py-1 rounded-2 fw-medium" style={{ fontSize: '11px' }}>
                                    {d.session_id}
                                </code>
                                <button 
                                    className="btn btn-link p-0 text-muted border-0 shadow-none hover-primary" 
                                    onClick={() => copyToClipboard(d.session_id)}
                                    title="Copy Session ID"
                                >
                                    <i className="bi bi-clipboard2-fill" style={{ fontSize: '14px' }}></i>
                                </button>
                            </div>
                          </td>
                          <td>
                            <span className={`badge rounded-pill fw-bold ${isOnline ? 'bg-success-subtle text-success' : 'bg-light text-muted'} px-3 py-2`} style={{ fontSize: '10px' }}>
                              <span className={`pulse-dot me-2 ${isOnline ? 'bg-success' : 'bg-secondary'}`}></span>
                              {isOnline ? 'ACTIVE' : 'OFFLINE'}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-1">
                              <button className="btn btn-light-indigo btn-sm border-0" title="Chatbot" onClick={() => setActiveChatbot(d)}><i className="bi bi-robot"></i></button>
                              <button className="btn btn-light-success btn-sm border-0" title="Schedule" onClick={() => setschedule(d)}><i className="bi bi-calendar-event"></i></button>
                              <button className="btn btn-light-purple btn-sm border-0" title="Logs" onClick={() => setViewingLogs(d)}><i className="bi bi-journal-text"></i></button>
                            </div>
                          </td>
                          <td className="pe-4 text-end">
                            {/* <button 
                              className={`btn btn-sm rounded-pill px-3 fw-bold border-0 ${isOnline ? 'btn-primary' : 'btn-light text-muted opacity-50'}`} 
                              style={{ fontSize: '11px' }}
                              disabled={!isOnline}
                              onClick={() => setSelectedDevice(d)}
                            >
                              SEND MESSAGE
                            </button> */}
                            {isOnline ? (
  <button 
    className="btn btn-sm rounded-pill px-3 fw-bold border-0 btn-primary"
    style={{ fontSize: '11px' }}
    onClick={() => setSelectedDevice(d)}
  >
    SEND MESSAGE
  </button>
) : (
  <button 
    className="btn btn-sm rounded-pill px-3 fw-bold border-0 btn-warning text-dark"
    style={{ fontSize: '11px' }}
    onClick={() => setReconnectDevice(d)}
  >
    RECONNECT
  </button>
)}

                            <button 
                                className="btn btn-sm text-danger border-0 ms-1" 
                                onClick={() => handleDisconnect(d.session_id, d.id)}
                            >
                                <i className="bi bi-power fs-5"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </PageTransition>

      {/* MODALS */}
      {showModal && <AddDeviceModal onClose={() => { setShowModal(false); fetchDevices(); }} />}
      {selectedDevice && <SendMessageModal device={selectedDevice} onClose={() => setSelectedDevice(null)} />}
      {viewingLogs && <MessageLogModal device={viewingLogs} onClose={() => setViewingLogs(null)} />}
      {activeChatbot && <ChatbotModal device={activeChatbot} onClose={() => setActiveChatbot(null)} />}
      {schedule && <ScheduleModal device={schedule} onClose={() => setschedule(null)} />}
      {reconnectDevice && (
  <AddDeviceModal
    device={reconnectDevice}
    reconnect
    onClose={() => {
      setReconnectDevice(null);
      fetchDevices();
    }}
  />
)}

    </Layout>
  );
}